import { useCallback, useEffect, useState } from 'react';
import { useGlobalStore } from '@digiwedge/global-state';
import { dismissReviewPrompt } from '@digiwedge/teetime-api-client';
/**
 * Hook to manage pending review prompts for the current player.
 * Automatically fetches pending prompts when page becomes visible.
 */
export function usePendingReviewPrompt(options = {}) {
    const { enabled = true } = options;
    const isAuthenticated = useGlobalStore((s) => s.isAuthenticated);
    const [prompts, setPrompts] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const currentPrompt = prompts[currentIndex] ?? null;
    const fetchPrompts = useCallback(async () => {
        if (!isAuthenticated || !enabled) {
            setPrompts([]);
            setCurrentIndex(0);
            return;
        }
        setLoading(true);
        try {
            // Temporarily disable fetching pending review prompts until the UAT backend exposes the endpoint.
            setPrompts([]);
            setCurrentIndex(0);
        }
        catch (error) {
            console.error('Error fetching pending review prompts:', error);
        }
        finally {
            setLoading(false);
        }
    }, [isAuthenticated, enabled]);
    // Fetch on mount
    useEffect(() => {
        void fetchPrompts();
    }, [fetchPrompts]);
    // Fetch when page becomes visible
    useEffect(() => {
        if (!enabled || typeof document === 'undefined')
            return;
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void fetchPrompts();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [enabled, fetchPrompts]);
    const handleDismissCurrentPrompt = useCallback(async () => {
        if (!currentPrompt)
            return;
        try {
            await dismissReviewPrompt(currentPrompt.bookingId);
            setPrompts((prev) => prev.filter((p) => p.bookingId !== currentPrompt.bookingId));
            setCurrentIndex((prev) => Math.min(prev, Math.max(0, prompts.length - 2)));
        }
        catch (error) {
            console.error('Error dismissing review prompt:', error);
        }
    }, [currentPrompt, prompts.length]);
    const skipToNextPrompt = useCallback(() => {
        if (currentIndex < prompts.length - 1) {
            setCurrentIndex((prev) => prev + 1);
        }
    }, [currentIndex, prompts.length]);
    return {
        currentPrompt,
        allPrompts: prompts,
        loading,
        dismissCurrentPrompt: handleDismissCurrentPrompt,
        skipToNextPrompt,
        refresh: fetchPrompts,
    };
}
//# sourceMappingURL=usePendingReviewPrompt.js.map